import { mockedCurrentUser } from "@/lib/data/factories/current-user-factory";
import type { User } from "@/lib/types";
import { faker, fakerEN_US } from "@faker-js/faker";
import dayjs from "dayjs";
import {
  type createContactAddressV1,
  type createContactEmailV1,
  type createContactFaxV1,
  type createContactNoteV1,
  type createContactNoteCommentV1,
  type createContactPhoneV1,
  type createContactV1,
  type createContactWebsiteV1,
} from "../requests";
import {
  CONTACT_ADDRESS_LABELS,
  CONTACT_EMAIL_LABELS,
  CONTACT_FAX_LABELS,
  CONTACT_INDIVIDUAL_SUBTYPES,
  CONTACT_ORGANIZATION_SUBTYPES,
  CONTACT_PHONE_LABELS,
  CONTACT_WEBSITE_LABELS,
  GENDERS,
  LANGUAGES,
  RACES,
} from "../static/contactStatics";

const localizedFaker = fakerEN_US;

export const generateMockContactKeys = () => {
  const contactKeys: {
    pk: string;
    sk: string;
    contactId: string;
    firmId: string;
    dateCreated: string;
    dateModified: string;
  } = {
    pk: faker.string.uuid(),
    sk: faker.string.uuid(),
    contactId: faker.string.uuid(),
    firmId: faker.string.uuid(),
    dateCreated: dayjs(faker.date.past())
      .format("YYYY-MM-DDTHH:mm:ss.SSSZ")
      .toString(),
    dateModified: dayjs(faker.date.past())
      .format("YYYY-MM-DDTHH:mm:ss.SSSZ")
      .toString(),
  };
  return contactKeys;
};

export const generateContactIndividual = () => {
  const contact: Omit<Parameters<typeof createContactV1>[0], "contactId"> = {
    individualFirstName: localizedFaker.person.firstName(),
    type: "individual",
    individualLastName: localizedFaker.person.lastName(),
    individualMiddleName: localizedFaker.person.middleName(),
    individualNickname: localizedFaker.helpers.maybe(
      () => {
        return faker.hacker.noun();
      },
      {
        probability: 0.2,
      },
    ),
    individualDateOfBirth: dayjs(
      localizedFaker.date.birthdate({ mode: "age", min: 18, max: 65 }),
    ).format("YYYY-MM-DD"),
    individualGender: localizedFaker.helpers.arrayElement(GENDERS),
    individualRace: localizedFaker.helpers.arrayElements(RACES),
    individualPrimaryLanguage: localizedFaker.helpers.arrayElement(LANGUAGES),
    individualSecondaryLanguage:
      localizedFaker.helpers.arrayElements(LANGUAGES),
    individualDeceased: localizedFaker.helpers.maybe(() => true, {
      probability: 0.1,
    }),
    individualSocialSecurityNumber: String(
      localizedFaker.helpers.rangeToNumber({
        min: 100000000,
        max: 999999999,
      }),
    ),
    individualSubTypes: localizedFaker.helpers.maybe(
      () => localizedFaker.helpers.arrayElements(CONTACT_INDIVIDUAL_SUBTYPES),
      {
        probability: 0.75,
      },
    ),
  };

  return contact;
};

export const generateContactOrganization = () => {
  const contact: Parameters<typeof createContactV1>[0] = {
    organizationName: localizedFaker.company.name(),
    type: "organization",
    organizationSubTypes: localizedFaker.helpers.maybe(
      () => localizedFaker.helpers.arrayElements(CONTACT_ORGANIZATION_SUBTYPES),
      {
        probability: 0.75,
      },
    ),
    organizationFoundingYear: String(
      localizedFaker.helpers.rangeToNumber({
        min: 1900,
        max: dayjs().year(),
      }),
    ),
    organizationTaxId: String(
      localizedFaker.helpers.rangeToNumber({
        min: 100000000,
        max: 999999999,
      }),
    ),
  };

  return contact;
};

export const generateContactAddress = () => {
  const addresses: Omit<
    Parameters<typeof createContactAddressV1>[0],
    "contactId"
  > = {
    city: localizedFaker.location.city(),
    state: localizedFaker.location.state(),
    zipCode: localizedFaker.location.zipCode(),
    country: "United States",
    label: localizedFaker.helpers.arrayElement(CONTACT_ADDRESS_LABELS),
    street1: localizedFaker.location.streetAddress(),
    street2: localizedFaker.helpers.maybe(
      () => {
        return faker.location.secondaryAddress();
      },
      {
        probability: 0.2,
      },
    ),
    description: localizedFaker.helpers.maybe(
      () => {
        return faker.lorem.sentence();
      },
      {
        probability: 0.4,
      },
    ),
  };
  return addresses;
};

export const generateContactEmail = () => {
  const emails: Omit<Parameters<typeof createContactEmailV1>[0], "contactId"> =
    {
      label: fakerEN_US.helpers.arrayElement(CONTACT_EMAIL_LABELS),
      address: fakerEN_US.internet.email(),
      description: fakerEN_US.helpers.maybe(
        () => {
          return faker.lorem.sentence();
        },
        {
          probability: 0.4,
        },
      ),
    };
  return emails;
};

export const generateContactPhone = () => {
  const phones: Omit<Parameters<typeof createContactPhoneV1>[0], "contactId"> =
    {
      label: fakerEN_US.helpers.arrayElement(CONTACT_PHONE_LABELS),
      number:
        "+" +
        String(
          faker.helpers.rangeToNumber({
            min: 12000000000,
            max: 19999999999,
          }),
        ),
      extension: fakerEN_US.helpers.maybe(
        () => {
          return (
            "+" +
            String(
              faker.helpers.rangeToNumber({
                min: 1,
                max: 999,
              }),
            )
          );
        },
        {
          probability: 0.25,
        },
      ),
      description: fakerEN_US.helpers.maybe(
        () => {
          return faker.lorem.sentence();
        },
        {
          probability: 0.4,
        },
      ),
    };
  return phones;
};

export const generateContactFax = () => {
  const faxes: Omit<Parameters<typeof createContactFaxV1>[0], "contactId"> = {
    label: fakerEN_US.helpers.arrayElement(CONTACT_FAX_LABELS),
    number:
      "+" +
      String(
        faker.helpers.rangeToNumber({
          min: 12000000000,
          max: 19999999999,
        }),
      ),
    extension: fakerEN_US.helpers.maybe(
      () => {
        return (
          "+" +
          String(
            faker.helpers.rangeToNumber({
              min: 1,
              max: 999,
            }),
          )
        );
      },
      {
        probability: 0.25,
      },
    ),
    description: fakerEN_US.helpers.maybe(
      () => {
        return faker.lorem.sentence();
      },
      {
        probability: 0.4,
      },
    ),
  };
  return faxes;
};

export const generateContactWebsite = () => {
  const websites: Omit<
    Parameters<typeof createContactWebsiteV1>[0],
    "contactId"
  > = {
    address: fakerEN_US.internet.url(),
    label: fakerEN_US.helpers.arrayElement(CONTACT_WEBSITE_LABELS),
    description: fakerEN_US.helpers.maybe(
      () => {
        return faker.lorem.sentence();
      },
      {
        probability: 0.4,
      },
    ),
  };
  return websites;
};

export const generateContactNote = () => {
  const draft = faker.helpers.maybe(() => true, {
    probability: 0.2,
  });
  const publishedDate = draft
    ? undefined
    : faker.helpers.maybe(
        () =>
          dayjs(faker.date.recent())
            .format("YYYY-MM-DDTHH:mm:ss.SSSZ")
            .toString(),
        {
          probability: 0.8,
        },
      );
  const notes: Omit<Parameters<typeof createContactNoteV1>[0], "contactId"> & {
    author: User;
    authorUserId: string;
    publishedDate: string | undefined;
  } = {
    message: fakerEN_US.lorem.paragraph(),
    draft: draft ?? false,
    publishedDate,
    author: mockedCurrentUser().data,
    authorUserId: mockedCurrentUser().data.id,
  };
  return notes;
};

export const generateContactNoteComment = (
  noteId: string,
): Omit<
  Parameters<typeof createContactNoteCommentV1>[0],
  "contactId" | "noteId"
> & {
  noteId: string;
  author: User;
  authorUserId: string;
  publishedDate: string;
} => {
  return {
    noteId,
    message: fakerEN_US.lorem.sentence(),
    draft: faker.helpers.maybe(() => true, {
      probability: 0.2,
    }),
    publishedDate: dayjs(faker.date.recent())
      .format("YYYY-MM-DDTHH:mm:ss.SSSZ")
      .toString(),
    author: mockedCurrentUser().data,
    authorUserId: mockedCurrentUser().data.id,
  };
};

export const generateContactWithSubItems = () => {
  const contactIndividual = generateContactIndividual();
  const contactOrganization = generateContactOrganization();
  const isIndividual = faker.helpers.maybe(() => true, {
    probability: 0.75,
  });
  const contact = isIndividual ? contactIndividual : contactOrganization;

  const contactAddress1 = generateContactAddress();
  const contactAddress2 = faker.helpers.maybe(() => generateContactAddress(), {
    probability: 0.3,
  });
  const contactAddress3 =
    contactAddress2 &&
    faker.helpers.maybe(() => generateContactAddress(), {
      probability: 0.1,
    });
  const contactAddresses = [contactAddress1];
  if (contactAddress2) {
    contactAddresses.push(contactAddress2);
  }
  if (contactAddress3) {
    contactAddresses.push(contactAddress3);
  }

  const contactEmail1 = generateContactEmail();
  const contactEmail2 = faker.helpers.maybe(() => generateContactEmail(), {
    probability: 0.3,
  });
  const contactEmail3 =
    contactEmail2 &&
    faker.helpers.maybe(() => generateContactEmail(), {
      probability: 0.1,
    });

  const contactEmails = [contactEmail1];
  if (contactEmail2) {
    contactEmails.push(contactEmail2);
  }
  if (contactEmail3) {
    contactEmails.push(contactEmail3);
  }
  const contactFax1 = generateContactFax();
  const contactFax2 = faker.helpers.maybe(() => generateContactFax(), {
    probability: 0.3,
  });
  const contactFax3 =
    contactFax2 &&
    faker.helpers.maybe(() => generateContactFax(), {
      probability: 0.1,
    });
  const contactFaxes = [contactFax1];
  if (contactFax2) {
    contactFaxes.push(contactFax2);
  }
  if (contactFax3) {
    contactFaxes.push(contactFax3);
  }
  const contactPhone1 = generateContactPhone();
  const contactPhone2 = faker.helpers.maybe(() => generateContactPhone(), {
    probability: 0.3,
  });
  const contactPhone3 =
    contactPhone2 &&
    faker.helpers.maybe(() => generateContactPhone(), {
      probability: 0.1,
    });
  const contactPhones = [contactPhone1];
  if (contactPhone2) {
    contactPhones.push(contactPhone2);
  }
  if (contactPhone3) {
    contactPhones.push(contactPhone3);
  }
  const contactNote1 = generateContactNote();
  const contactNote2 = faker.helpers.maybe(() => generateContactNote(), {
    probability: 0.3,
  });
  const contactNote3 =
    contactNote2 &&
    faker.helpers.maybe(() => generateContactNote(), {
      probability: 0.1,
    });
  const contactNotes = [contactNote1];
  if (contactNote2) {
    contactNotes.push(contactNote2);
  }
  if (contactNote3) {
    contactNotes.push(contactNote3);
  }
  const contactWebsite1 = generateContactWebsite();
  const contactWebsite2 = faker.helpers.maybe(() => generateContactWebsite(), {
    probability: 0.3,
  });
  const contactWebsite3 =
    contactWebsite2 &&
    faker.helpers.maybe(() => generateContactWebsite(), {
      probability: 0.1,
    });
  const contactWebsites = [contactWebsite1];
  if (contactWebsite2) {
    contactWebsites.push(contactWebsite2);
  }
  if (contactWebsite3) {
    contactWebsites.push(contactWebsite3);
  }
  return {
    contact,
    addresses: contactAddresses,
    emails: contactEmails,
    faxes: contactFaxes,
    phones: contactPhones,
    notes: contactNotes,
    websites: contactWebsites,
  };
};
